/**
 * 组帧：把字段值变成一串可以写进串口或 WebSocket 的字节。
 *
 * 载荷长度的规则**不是"总是发满"**，两种版本的行为不同（与上游 C 库一致）：
 *
 *   - MAVLink 1：只发基础字段（`baseLength`）。扩展字段在 v1 里不存在，
 *     发出去老接收端会把它们当成下一个字段。
 *   - MAVLink 2：发完整载荷，但**去掉尾部零字节**（至少保留 1 字节）。
 *     收端负责零填充，所以这是无损的，而 STATUSTEXT 这类大量零填充的消息能省掉一半字节。
 */
import { crc16X25, crcAccumulate } from './crc';
import { compileMessage } from './descriptor';
import {
  MAVLINK_CORE_HEADER_LENGTH_V1,
  MAVLINK_CORE_HEADER_LENGTH_V2,
  MAVLINK_IFLAG_SIGNED,
  MAVLINK_MAX_PAYLOAD_LENGTH,
  MAVLINK_SIGNATURE_BLOCK_LENGTH,
  MAVLINK_STX_V1,
  MAVLINK_STX_V2,
  MAX_SEQUENCE,
  MavlinkEncodeError,
} from './frame';
import { encodePayload, type MessageValues } from './payload';
import type { MavlinkSigner } from './signing';
import type { MessageDefinition } from './types';

export interface EncodeFrameOptions {
  /** 默认 2。只有对端确实不支持 MAVLink 2 时才用 1。 */
  readonly version?: 1 | 2;
  /** 发送序号（0–255）。由会话层按 (sysid, compid, 通道) 维护并递增。 */
  readonly sequence: number;
  readonly systemId: number;
  readonly componentId: number;
  /** 配置后帧会被签名（仅 MAVLink 2） */
  readonly signer?: MavlinkSigner;
}

function checkByte(value: number, name: string): number {
  if (!Number.isInteger(value) || value < 0 || value > 255) {
    throw new MavlinkEncodeError(`${name} 必须是 0–255 之间的整数，收到 ${value}`);
  }
  return value;
}

/** MAVLink 2 的尾部零字节截断；至少保留 1 字节（`len = 0` 不被允许）。 */
export function trimTrailingZeros(payload: Uint8Array): Uint8Array {
  let length = payload.length;
  while (length > 1 && payload[length - 1] === 0) length -= 1;
  return length === payload.length ? payload : payload.subarray(0, length);
}

/** 计算一帧的 `(sysid, compid, 通道)` 下一次要用的序号。序号是 8 位，回绕。 */
export function nextSequence(sequence: number): number {
  return (sequence + 1) % (MAX_SEQUENCE + 1);
}

export function encodeFrame(
  message: MessageDefinition,
  values: MessageValues,
  options: EncodeFrameOptions,
): Uint8Array {
  const version = options.version ?? 2;
  const sequence = checkByte(options.sequence, '发送序号');
  const systemId = checkByte(options.systemId, '系统 ID');
  const componentId = checkByte(options.componentId, '组件 ID');

  if (version === 2 && message.id > 0xffffff) {
    throw new MavlinkEncodeError(`${message.name} 的消息 ID ${message.id} 超出 MAVLink 2 的 24 位上限`);
  }
  if (version === 1 && message.id > 0xff) {
    throw new MavlinkEncodeError(
      `${message.name} 的消息 ID ${message.id} 超出 MAVLink 1 的 8 位上限，这条消息只能走 MAVLink 2`,
    );
  }

  const compiled = compileMessage(message);
  const full = encodePayload(message, values);
  const payload =
    version === 1 ? full.subarray(0, compiled.baseLength) : trimTrailingZeros(full);

  if (payload.length > MAVLINK_MAX_PAYLOAD_LENGTH) {
    throw new MavlinkEncodeError(
      `${message.name} 的载荷 ${payload.length} 字节超出 MAVLink 的上限 ${MAVLINK_MAX_PAYLOAD_LENGTH}`,
    );
  }

  const signing = version === 2 && options.signer !== undefined;
  const headerLength = version === 2 ? MAVLINK_CORE_HEADER_LENGTH_V2 : MAVLINK_CORE_HEADER_LENGTH_V1;
  const totalLength =
    1 + headerLength + payload.length + 2 + (signing ? MAVLINK_SIGNATURE_BLOCK_LENGTH : 0);

  const frame = new Uint8Array(totalLength);
  frame[0] = version === 2 ? MAVLINK_STX_V2 : MAVLINK_STX_V1;
  frame[1] = payload.length;

  if (version === 2) {
    frame[2] = signing ? MAVLINK_IFLAG_SIGNED : 0;
    frame[3] = 0; // compat_flags
    frame[4] = sequence;
    frame[5] = systemId;
    frame[6] = componentId;
    frame[7] = message.id & 0xff;
    frame[8] = (message.id >> 8) & 0xff;
    frame[9] = (message.id >> 16) & 0xff;
  } else {
    frame[2] = sequence;
    frame[3] = systemId;
    frame[4] = componentId;
    frame[5] = message.id & 0xff;
  }

  frame.set(payload, 1 + headerLength);

  const crcStart = 1;
  const crcEnd = crcStart + headerLength + payload.length;
  let crc = crc16X25(frame, crcStart, crcEnd);
  crc = crcAccumulate(crc, message.crcExtra);
  frame[crcEnd] = crc & 0xff;
  frame[crcEnd + 1] = (crc >> 8) & 0xff;

  if (signing && options.signer !== undefined) {
    // 签名覆盖"STX 之后、签名之前"的全部字节
    const body = frame.subarray(1, crcEnd + 2);
    const block = options.signer.sign(body);
    if (block.length !== MAVLINK_SIGNATURE_BLOCK_LENGTH) {
      throw new MavlinkEncodeError('签名器返回的签名块长度不是 13 字节');
    }
    frame.set(block, crcEnd + 2);
  }

  return frame;
}
